/**
 * The core bridge: recipe document → `calculateRecipe` inputs.
 *
 * ```ts
 * const { recipe, equipment } = toCalculationInputs(doc, profile);
 * const vitals = calculateRecipe(recipe, equipment).vitals;
 * ```
 *
 * Mechanically this is one translation: the document model spells optional
 * values `T | null` and always carries the key (so JSON round-trips exactly),
 * while core spells them `T?` under `exactOptionalPropertyTypes` (so an absent
 * value means "apply the engine's documented default"). {@link optional} is
 * that translation, and it is the only conversion here — every *number* in a
 * recipe document is already in core's canonical units, because the snapshot
 * bridge put it there.
 *
 * Nothing is computed on the way through. If a vital looks wrong, it is wrong
 * in the document or in the engine, never in between. The single value that is
 * *chosen* rather than copied is the mash efficiency, where a recipe may
 * override its profile — see {@link effectiveMashEfficiencyPercent}.
 */

import type {
  AcidAddition,
  DecoctionSpec,
  EquipmentProfile,
  FermentableAddition,
  FermentationStep,
  HopAddition,
  MashStep,
  RecipeInput,
  RecipeWaterInput,
  SaltAddition,
  WaterIon,
  WaterProfile,
  YeastSelection,
} from '@beerwright/core';
import type { EquipmentProfileDoc } from '../document/equipment';
import type {
  RecipeAcidAddition,
  RecipeDecoction,
  RecipeFermentable,
  RecipeFermentationStep,
  RecipeHop,
  RecipeMashStep,
  RecipeSaltAddition,
  RecipeYeast,
} from '../document/ingredients';
import type { RecipeDoc } from '../document/recipe';
import type { OptionalDocIon, RecipeWater, WaterProfileDoc } from '../document/water';
import { OPTIONAL_DOC_IONS } from '../document/water';
import type { WorkspaceState } from '../document/workspace';

/**
 * `{ key: value }` when `value` is non-null, `{}` when it is null.
 *
 * The document/core optionality translation, in one place. Spreading the result
 * keeps the property genuinely absent rather than present-and-`undefined`,
 * which is what `exactOptionalPropertyTypes` requires and what makes core's
 * `?? default` fallbacks fire.
 */
function optional<K extends string, V>(key: K, value: V | null): { [P in K]?: V } {
  return (value === null ? {} : { [key]: value }) as { [P in K]?: V };
}

/** Recipe fermentable → core `FermentableAddition`. */
export function toFermentableAddition(item: RecipeFermentable): FermentableAddition {
  return {
    id: item.itemId,
    name: item.name,
    amountKg: item.amountKg,
    class: item.class,
    use: item.use,
    extractFineGrindDryBasisPercent: item.extractFineGrindDryBasisPercent,
    moisturePercent: item.moisturePercent,
    colorLovibond: item.colorLovibond,
    diastaticPowerLintner: item.diastaticPowerLintner,
    ...optional('ppg', item.ppgOverride),
    ...optional('fineCoarseDifferencePercent', item.fineCoarseDifferencePercent),
    ...optional('fermentabilityPercent', item.fermentabilityPercentOverride),
    ph: {
      maltClass: item.maltClass,
      ...optional('distilledWaterPh', item.distilledWaterPh),
      ...optional('bufferingCapacityMEqPerKgPerPh', item.bufferingCapacityMEqPerKgPerPh),
      ...optional('acidContentPercentLactic', item.acidContentPercentLactic),
    },
  };
}

/** Recipe hop → core `HopAddition`. */
export function toHopAddition(item: RecipeHop): HopAddition {
  return {
    id: item.itemId,
    name: item.name,
    amountKg: item.amountKg,
    alphaAcidPercent: item.alphaAcidPercent,
    form: item.form,
    use: item.use,
    timeMinutes: item.timeMinutes,
    ...optional('dryHopDays', item.dryHopDays),
    ...optional('whirlpoolTempC', item.whirlpoolTempC),
    ...optional('whirlpoolEndTempC', item.whirlpoolEndTempC),
    ...optional('betaAcidPercent', item.betaAcidPercent),
    ...optional('totalOilMlPer100g', item.totalOilMlPer100g),
  };
}

/**
 * Recipe yeast → core `YeastSelection`.
 *
 * `manufactureDate` is the one type change in the whole bridge: documents store
 * an ISO string (JSON has no date), core wants a `Date` for the viability decay
 * model. Parsed as UTC midnight, which is what a bare `YYYY-MM-DD` means.
 */
export function toYeastSelection(item: RecipeYeast): YeastSelection {
  return {
    id: item.itemId,
    name: item.name,
    type: item.type,
    form: item.form,
    attenuationMinPercent: item.attenuationMinPercent,
    attenuationMaxPercent: item.attenuationMaxPercent,
    ...optional('attenuationPercent', item.attenuationPercentOverride),
    packs: item.packs,
    ...optional('cellsPerPackBillion', item.cellsPerPackBillion),
    ...optional('gramsPerPack', item.gramsPerPack),
    ...optional('cellsPerGramBillion', item.cellsPerGramBillion),
    ...optional('temperatureMinC', item.temperatureMinC),
    ...optional('temperatureMaxC', item.temperatureMaxC),
    ...optional(
      'manufactureDate',
      item.manufactureDate === null ? null : new Date(`${item.manufactureDate}T00:00:00.000Z`),
    ),
    // A row written before the field existed has no key at all, which `?? null`
    // turns into the same "no starter" the engine sees for an explicit `null`.
    ...optional('starter', item.starter ?? null),
  };
}

/** Recipe mash step → core `MashStep`. */
export function toMashStep(step: RecipeMashStep): MashStep {
  return {
    name: step.name,
    type: step.type,
    targetTempC: step.targetTempC,
    timeMinutes: step.timeMinutes,
    ...optional('infusionVolumeL', step.infusionVolumeL),
    ...optional('infusionTempC', step.infusionTempC),
    ...optional('thicknessLPerKg', step.thicknessLPerKg),
    ...optional('decoction', step.decoction === null ? null : toDecoctionSpec(step.decoction)),
  };
}

/**
 * Recipe decoction → core `DecoctionSpec`.
 *
 * The document keeps every key present and uses `null` for "unspecified"; core
 * uses absent, because its own defaults are the fallback and
 * `exactOptionalPropertyTypes` will not take an explicit `undefined`. That is
 * the same translation `optional` performs everywhere else in this file.
 */
function toDecoctionSpec(decoction: RecipeDecoction): DecoctionSpec {
  return {
    thickness: decoction.thickness,
    ...optional('ratioLPerKg', decoction.ratioLPerKg),
    boilMinutes: decoction.boilMinutes,
    ...optional('restBeforeBoil', decoction.restBeforeBoil),
    ...optional('pullVolumeL', decoction.pullVolumeL),
  };
}

/** Recipe fermentation step → core `FermentationStep`. */
export function toFermentationStep(step: RecipeFermentationStep): FermentationStep {
  return {
    name: step.name,
    type: step.type,
    tempC: step.tempC,
    days: step.days,
    ...optional('endTempC', step.endTempC),
    ...optional('pressurePsi', step.pressurePsi),
  };
}

/** Which core ion each nullable document field carries. */
const OPTIONAL_ION_OF: Readonly<Record<OptionalDocIon, WaterIon>> = {
  sodiumPpm: 'sodium',
  chloridePpm: 'chloride',
  sulfatePpm: 'sulfate',
};

/**
 * Water profile document → core `WaterProfile`.
 *
 * The one place a document's `null` ion becomes core's zero-plus-provenance.
 * The engine gets the number it has always got — nothing downstream of here
 * grows a null branch — and `unmeasured` carries the fact that the number was
 * never a reading, so the parts of core that make claims to a brewer can
 * decline to make them. See `WaterProfile.unmeasured`.
 */
export function toWaterProfile(profile: WaterProfileDoc): WaterProfile {
  const unmeasured = OPTIONAL_DOC_IONS.filter((key) => profile[key] === null).map(
    (key) => OPTIONAL_ION_OF[key],
  );
  return {
    ...optional('name', profile.name),
    calciumPpm: profile.calciumPpm,
    magnesiumPpm: profile.magnesiumPpm,
    sodiumPpm: profile.sodiumPpm ?? 0,
    chloridePpm: profile.chloridePpm ?? 0,
    sulfatePpm: profile.sulfatePpm ?? 0,
    bicarbonatePpm: profile.bicarbonatePpm,
    ...optional('ph', profile.ph),
    ...(unmeasured.length > 0 ? { unmeasured } : {}),
  };
}

/** Salt addition document → core `SaltAddition`. */
export function toSaltAddition(addition: RecipeSaltAddition): SaltAddition {
  return {
    salt: addition.salt,
    grams: addition.grams,
    target: addition.target,
    ...optional('dissolvedFraction', addition.dissolvedFraction),
  };
}

/** Acid addition document → core `AcidAddition`. */
export function toAcidAddition(addition: RecipeAcidAddition): AcidAddition {
  return {
    acid: addition.acid,
    ...optional('millilitres', addition.millilitres),
    ...optional('grams', addition.grams),
    ...optional('concentrationPercent', addition.concentrationPercent),
    target: addition.target,
  };
}

/** Water section document → core `RecipeWaterInput`. */
export function toRecipeWaterInput(water: RecipeWater): RecipeWaterInput {
  return {
    sourceProfile: toWaterProfile(water.sourceProfile),
    dilutionFraction: water.dilutionFraction,
    ...optional(
      'dilutionProfile',
      water.dilutionProfile === null ? null : toWaterProfile(water.dilutionProfile),
    ),
    ...optional(
      'spargeSourceProfile',
      water.spargeSourceProfile === null ? null : toWaterProfile(water.spargeSourceProfile),
    ),
    saltAdditions: water.saltAdditions.map(toSaltAddition),
    acidAdditions: water.acidAdditions.map(toAcidAddition),
    ...optional(
      'targetProfile',
      water.targetProfile === null ? null : toWaterProfile(water.targetProfile),
    ),
    targetMashPh: water.targetMashPh,
    acidifySparge: water.acidifySparge,
    ...optional('spargeTargetPh', water.spargeTargetPh),
    ...optional('spargeAcid', water.spargeAcid),
    ...optional('spargeAcidConcentrationPercent', water.spargeAcidConcentrationPercent),
  };
}

/** Equipment profile document → core `EquipmentProfile`. */
export function toEquipmentProfile(profile: EquipmentProfileDoc): EquipmentProfile {
  return {
    name: profile.name,
    spargeType: profile.spargeType,
    mashTunDeadspaceL: profile.mashTunDeadspaceL,
    mashTunLossL: profile.mashTunLossL,
    grainAbsorptionLPerKg: profile.grainAbsorptionLPerKg,
    mashThicknessLPerKg: profile.mashThicknessLPerKg,
    boilOffRateLPerHour: profile.boilOffRateLPerHour,
    boilShrinkagePercent: profile.boilShrinkagePercent,
    kettleDeadspaceL: profile.kettleDeadspaceL,
    hopAbsorptionMlPerG: profile.hopAbsorptionMlPerG,
    dryHopAbsorptionMlPerG: profile.dryHopAbsorptionMlPerG,
    chillerLossL: profile.chillerLossL,
    fermenterTrubLossL: profile.fermenterTrubLossL,
    topUpKettleL: profile.topUpKettleL,
    topUpFermenterL: profile.topUpFermenterL,
    mashEfficiencyPercent: profile.mashEfficiencyPercent,
    brewhouseEfficiencyPercent: profile.brewhouseEfficiencyPercent,
    mashTunThermalMassLEquivalent: profile.mashTunThermalMassLEquivalent,
    mashTunInitialTempC: profile.mashTunInitialTempC,
    mashStandingLossCPerHour: profile.mashStandingLossCPerHour,
    decoctionBoilOffLPerHour: profile.decoctionBoilOffLPerHour,
    ...optional('kettleVolumeL', profile.kettleVolumeL),
    ...optional('mashTunVolumeL', profile.mashTunVolumeL),
    ...optional('fermenterVolumeL', profile.fermenterVolumeL),
    ...optional('decoctionPotVolumeL', profile.decoctionPotVolumeL),
  };
}

/** Recipe document → core `RecipeInput`. */
export function toRecipeInput(doc: RecipeDoc): RecipeInput {
  return {
    name: doc.name,
    batchVolumeL: doc.batchVolumeL,
    batchVolumeBasis: doc.batchVolumeBasis,
    boilTimeMinutes: doc.boilTimeMinutes,
    fermentables: doc.fermentables.map(toFermentableAddition),
    hops: doc.hops.map(toHopAddition),
    yeasts: doc.yeasts.map(toYeastSelection),
    mashSteps: doc.mashSteps.map(toMashStep),
    fermentationSteps: doc.fermentationSteps.map(toFermentationStep),
    carbonationVolumes: doc.carbonationVolumes,
    ...optional('water', doc.water === null ? null : toRecipeWaterInput(doc.water)),
  };
}

/**
 * The mash efficiency this recipe actually calculates at, **%**.
 *
 * The recipe's `mashEfficiencyPercentOverride` when it has one, the profile's
 * figure otherwise. One function so that "what efficiency is this beer at?" has
 * a single answer — the maths, the header field, the brew sheet and the diff
 * all ask here rather than each re-deciding which number wins.
 *
 * @param doc - The recipe document.
 * @param equipment - The profile the recipe resolves to; see
 *   {@link resolveEquipmentProfileDoc}.
 */
export function effectiveMashEfficiencyPercent(
  doc: RecipeDoc,
  equipment: EquipmentProfileDoc,
): number {
  return doc.mashEfficiencyPercentOverride ?? equipment.mashEfficiencyPercent;
}

/**
 * Everything `calculateRecipe` needs, from one document and one profile.
 *
 * The recipe's efficiency override is applied *here*, on the way into the
 * engine, rather than being resolved earlier: every caller in the app reaches
 * the maths through this function — the editor, brew day, the brew sheet, the
 * gallery, the exporters, the assistant — so this is the one seam where the
 * override cannot be forgotten. Core never learns that an override exists; it
 * is handed a profile with the right number in it, which is why nothing
 * downstream (`vitals.mashEfficiencyPercent`, the predicted brewhouse figure,
 * the explain popovers) needed to change.
 *
 * @param doc - The recipe document.
 * @param equipment - The profile the recipe resolves to; see
 *   {@link resolveEquipmentProfileDoc}.
 */
export function toCalculationInputs(
  doc: RecipeDoc,
  equipment: EquipmentProfileDoc,
): { readonly recipe: RecipeInput; readonly equipment: EquipmentProfile } {
  return {
    recipe: toRecipeInput(doc),
    equipment: {
      ...toEquipmentProfile(equipment),
      mashEfficiencyPercent: effectiveMashEfficiencyPercent(doc, equipment),
    },
  };
}

/**
 * Which equipment profile a recipe actually calculates against.
 *
 * Falls back in order: the recipe's own `equipmentProfileId` → the workspace's
 * `defaultEquipmentProfileId` → the first built-in. A dangling id (the profile
 * was deleted) resolves the same way as `null`, which is what lets
 * `equipment.delete` stay a single-document change.
 *
 * @returns The profile, or `undefined` for a workspace with no profiles at all.
 */
export function resolveEquipmentProfileDoc(
  state: WorkspaceState,
  doc: RecipeDoc,
): EquipmentProfileDoc | undefined {
  const byRecipe =
    doc.equipmentProfileId === null ? undefined : state.equipmentProfiles[doc.equipmentProfileId];
  if (byRecipe !== undefined) return byRecipe;
  const defaultId = state.settings.defaultEquipmentProfileId;
  const byDefault = defaultId === null ? undefined : state.equipmentProfiles[defaultId];
  if (byDefault !== undefined) return byDefault;
  return Object.values(state.equipmentProfiles)[0];
}
